import React, { useRef, useState } from 'react';
import { api } from '../api/client';

const DATA_TYPES = [
  { value: 'security', label: 'Stock / ETF / Fund (prices)' },
  { value: 'money_market', label: 'Money Market (7-day yield)' },
];
const STATUS_LABELS = {
  invalid: 'Invalid',
  partial_day: 'Partial day',
  duplicate: 'Duplicate',
};

// Same rule as the API: the file name's leading token, up to the first space, '(' or '.'.
const symbolFromFilename = (name) => (name.match(/^([A-Za-z0-9-]+)/) || [])[1]?.toUpperCase() || '';

// Imports one symbol's daily history from a Fidelity CSV export. The file is staged
// and checked first; the preview shows what would load, and nothing reaches the
// data tables until the user confirms. `moneyMarketSymbols` preselects the data
// type; `onChanged` fires whenever an import is checked, loaded, discarded or
// rejected, so the page can refresh its list of imports.
export default function HistoryImport({ moneyMarketSymbols = [], onChanged }) {
  const fileInput = useRef(null);
  const [file, setFile] = useState(null);
  const [symbol, setSymbol] = useState('');
  const [dataType, setDataType] = useState('security');
  const [batch, setBatch] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);

  const changed = () => { if (onChanged) onChanged(); };

  const reset = () => {
    setFile(null);
    setSymbol('');
    setBatch(null);
    setError('');
    if (fileInput.current) fileInput.current.value = '';
  };

  const chooseFile = (f) => {
    setBatch(null);
    setError('');
    if (f && !/\.csv$/i.test(f.name)) {
      setFile(null);
      setError(`"${f.name}" is not a .csv file.`);
      return;
    }
    setFile(f || null);
    if (!f) return;
    const sym = symbolFromFilename(f.name);
    setSymbol(sym);
    setDataType(moneyMarketSymbols.some(m => m.symbol === sym) ? 'money_market' : 'security');
  };

  // Dropping a file and picking one in the file dialog lead to the same place.
  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;
    if (files.length > 1) {
      setFile(null);
      setError('Drop one file at a time: each file holds one symbol.');
      return;
    }
    chooseFile(files[0]);
  };

  const stage = async () => {
    setBusy(true);
    setError('');
    try {
      const content = await file.text();
      setBatch(await api.stageImport({ filename: file.name, content, symbol, data_type: dataType }));
      changed();
    } catch (e) {
      setError(e.message);
      changed();
    }
    setBusy(false);
  };

  const load = async () => {
    setBusy(true);
    setError('');
    try {
      setBatch(await api.loadImport(batch.id));
      changed();
    } catch (e) {
      setError(e.message);
      changed();
    }
    setBusy(false);
  };

  const discard = async () => {
    setBusy(true);
    try { await api.discardImport(batch.id); } catch {}
    setBusy(false);
    reset();
    changed();
  };

  const typeLabel = (t) => t === 'money_market' ? 'Money market' : 'Prices';
  const staged = batch && batch.status === 'staged';
  const c = batch ? batch.counts : null;

  return (
    <div className="card">
      <div className="modal-title">Import History</div>
      <div className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
        Load daily history for one symbol from a Fidelity CSV export. The file is checked first; no data is loaded until you confirm. Each uploaded file is kept on the NAS.
      </div>

      {!batch && (
        <>
          <div className="form-group">
            <label>File (one symbol per file; the file name starts with the symbol)</label>
            <div
              role="button"
              tabIndex={0}
              aria-label="Choose a history file, or drag and drop one here"
              onClick={() => fileInput.current && fileInput.current.click()}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.current && fileInput.current.click(); } }}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragEnter={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              style={{
                border: `2px dashed ${dragging ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 8,
                padding: '22px 16px',
                textAlign: 'center',
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              {file
                ? <><strong>{file.name}</strong><div className="muted" style={{ marginTop: 4 }}>Drop a different file here, or click to choose another</div></>
                : <>Drag and drop a CSV file here<div className="muted" style={{ marginTop: 4 }}>or click to browse for one</div></>}
            </div>
            <input
              ref={fileInput}
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={e => { chooseFile(e.target.files[0]); e.target.value = ''; }}
            />
          </div>
          {file && (
            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 12 }}>
              <div className="form-group">
                <label>Symbol</label>
                <input value={symbol} onChange={e => setSymbol(e.target.value.toUpperCase())} />
              </div>
              <div className="form-group">
                <label>Type of data</label>
                <select value={dataType} onChange={e => setDataType(e.target.value)}>
                  {DATA_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>
          )}
          {error && <div className="loss" style={{ fontSize: 13, marginBottom: 12 }}>{error}</div>}
          {file && (
            <button className="btn" onClick={stage} disabled={busy || !symbol}>
              {busy ? 'Checking...' : 'Check File'}
            </button>
          )}
        </>
      )}

      {batch && (
        <>
          <div style={{ fontSize: 13, marginBottom: 12 }}>
            <strong className="mono">{batch.symbol}</strong> · {typeLabel(batch.data_type)} · <span className="muted">{batch.filename}</span>
            <div className="muted" style={{ marginTop: 4 }}>
              {batch.row_count} rows{batch.first_date ? `, ${batch.first_date} to ${batch.last_date}` : ''}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 12 }}>
            {[
              [staged ? 'To load' : 'Loaded', staged ? c.to_load : c.loaded, 'gain'],
              ['Duplicates', c.duplicate, ''],
              ['Invalid', c.invalid, c.invalid ? 'loss' : ''],
              ['Partial day', c.partial_day, ''],
            ].map(([label, n, cls]) => (
              <div key={label} className="stat-card" style={{ textAlign: 'right' }}>
                <div className="stat-label">{label}</div>
                <div className={`stat-value mono ${cls}`} style={{ fontSize: 16 }}>{n}</div>
              </div>
            ))}
          </div>

          {c.duplicate > 0 && (
            <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
              Duplicates are dates already stored for {batch.symbol}; they are never reloaded.{' '}
              {c.duplicate_differing > 0
                ? <span className="loss">{c.duplicate_differing} differ from the stored values (listed below).</span>
                : 'All match the stored values.'}
            </div>
          )}
          {batch.warnings.map((w, i) => (
            <div key={i} className="loss" style={{ fontSize: 13, marginBottom: 8 }}>{w}</div>
          ))}

          {batch.attention_rows && batch.attention_rows.length > 0 && (
            <div style={{ maxHeight: 260, overflowY: 'auto', marginBottom: 12 }}>
              <table>
                <thead>
                  <tr><th>Row</th><th>Date</th><th>Status</th><th>Notes</th></tr>
                </thead>
                <tbody>
                  {batch.attention_rows.map(r => (
                    <tr key={r.row_number}>
                      <td className="mono muted">{r.row_number}</td>
                      <td className="mono">{r.date || (r.raw && r.raw.date) || '—'}</td>
                      <td className={r.status === 'invalid' ? 'loss' : ''} style={{ whiteSpace: 'nowrap' }}>{STATUS_LABELS[r.status] || r.status}</td>
                      <td className="muted" style={{ fontSize: 12 }}>{r.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {batch.attention_rows_total > batch.attention_rows.length && (
                <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                  Showing {batch.attention_rows.length} of {batch.attention_rows_total} rows.
                </div>
              )}
            </div>
          )}

          {error && <div className="loss" style={{ fontSize: 13, marginBottom: 12 }}>{error}</div>}

          {staged ? (
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn" onClick={load} disabled={busy || c.to_load === 0}>
                {busy ? 'Working...' : c.to_load === 0 ? 'Nothing new to load' : `Load ${c.to_load} rows`}
              </button>
              <button className="btn-ghost" onClick={discard} disabled={busy}>Discard</button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <span style={{ fontSize: 13 }}>
                <span className="gain">Loaded {batch.loaded_count} rows for {batch.symbol}.</span>
                {c.invalid > 0 && <span className="muted"> {c.invalid} invalid rows were left out; the file is filed under processed-with-errors.</span>}
              </span>
              <button className="btn-ghost" style={{ marginLeft: 'auto' }} onClick={reset}>Import Another File</button>
            </div>
          )}
        </>
      )}

    </div>
  );
}
